import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PageHeader, Badge } from "../../components/ui-extras";
import { LockKey } from "@phosphor-icons/react";

function Row({ label, value, testId }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-zinc-100 last:border-b-0">
      <div className="overline text-[10px] text-zinc-500 pt-1">{label}</div>
      <div
        className="text-sm text-zinc-900 text-right font-mono max-w-[60%] break-words"
        data-testid={testId}
      >
        {value || "—"}
      </div>
    </div>
  );
}

export default function CustomerProfile() {
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.customer_id) {
      setLoading(false);
      return;
    }
    api
      .get(`/customers/${user.customer_id}`)
      .then(({ data }) => setCustomer(data))
      .catch((e) => setErr(e?.response?.status === 403
        ? "You don't have access to this profile."
        : "Could not load profile."))
      .finally(() => setLoading(false));
  }, [user?.customer_id]);

  return (
    <>
      <PageHeader
        title="Profile"
        subtitle="PORTAL / PROFILE"
        testId="portal-profile-header"
      />

      <div
        className="gf-card mb-4 border-l-4 border-zinc-300 bg-zinc-50/40"
        data-testid="portal-profile-readonly-notice"
      >
        <div className="gf-card-body flex items-center gap-3 text-sm text-zinc-600">
          <LockKey size={16} className="text-zinc-400" />
          Profile is read-only. To request a change, contact your account
          manager at hello@getfuelcard.com.
        </div>
      </div>

      {loading && (
        <div className="gf-card">
          <div className="gf-card-body text-sm text-zinc-400 text-center py-10">
            Loading profile…
          </div>
        </div>
      )}

      {err && !loading && (
        <div className="gf-card">
          <div
            className="gf-card-body text-sm text-rose-600"
            data-testid="portal-profile-error"
          >
            {err}
          </div>
        </div>
      )}

      {!loading && !customer && !err && (
        <div className="gf-card">
          <div
            className="gf-card-body text-sm text-zinc-600"
            data-testid="portal-profile-unlinked"
          >
            Your sign-in isn't linked to a customer account yet. Contact
            hello@getfuelcard.com to get linked.
          </div>
        </div>
      )}

      {customer && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="gf-card">
            <div className="gf-card-header"><h3>Company</h3></div>
            <div className="gf-card-body">
              <Row
                label="COMPANY NAME"
                value={customer.company_name}
                testId="portal-profile-company"
              />
              <Row
                label="CUSTOMER ID"
                value={customer.customer_id}
                testId="portal-profile-customer-id"
              />
              <Row
                label="STATUS"
                value={
                  <Badge variant={customer.active === false ? "muted" : "success"}>
                    {customer.active === false ? "inactive" : "active"}
                  </Badge>
                }
                testId="portal-profile-active"
              />
              <Row
                label="ADDRESS"
                value={customer.address}
                testId="portal-profile-address"
              />
              {/* PHASE-4-P4-N1 (2026-02) — surface the complete
                  canonical postal address (city, province/state,
                  postal/ZIP, country) already returned by
                  GET /customers/{id}. These fields exist on the
                  Customer schema (P3D5 additive fields) and are
                  edited from the Admin Preferences modal. Read-only
                  display parity — NO customer record is written or
                  mutated by this render. */}
              <Row
                label="CITY"
                value={customer.city}
                testId="portal-profile-city"
              />
              <Row
                label="PROVINCE / STATE"
                value={customer.province}
                testId="portal-profile-province"
              />
              <Row
                label="POSTAL / ZIP"
                value={customer.postal_code}
                testId="portal-profile-postal-code"
              />
              <Row
                label="COUNTRY"
                value={customer.country}
                testId="portal-profile-country"
              />
            </div>
          </div>

          <div className="gf-card">
            <div className="gf-card-header"><h3>Contact & billing</h3></div>
            <div className="gf-card-body">
              <Row
                label="CONTACT NAME"
                value={customer.contact_name}
                testId="portal-profile-contact-name"
              />
              <Row
                label="CONTACT EMAIL"
                value={customer.contact_email}
                testId="portal-profile-contact-email"
              />
              <Row
                label="BILLING EMAIL"
                value={customer.billing_email}
                testId="portal-profile-billing-email"
              />
              <Row
                label="PHONE"
                value={customer.phone}
                testId="portal-profile-phone"
              />
              <Row
                label="BILLING DAY"
                value={customer.billing_day}
                testId="portal-profile-billing-day"
              />
              <Row
                label="SIGNED IN AS"
                value={user?.email}
                testId="portal-profile-signed-in-email"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
